<template>
  <div class="product-list-page">
    <header-component />
    <div class="px-2 py-2 w-full">
      <div class="card">
        <div
          class="product-list-page__toolbar flex flex-wrap items-center justify-between gap-3 px-3 py-3 border-bottom bg-white"
        >
          <h3
            class="mb-0 font-bold text-gray-800 text-md"
            style="text-transform: capitalize"
          >
            Product List
          </h3>
          <div class="product-list-page__toolbar-right text-right">
            <div
              v-if="filters.length"
              class="table-filters product-list-page__filters"
            >
              <el-tag
                v-for="(tag, index) in filters"
                :key="index"
                :disable-transitions="false"
                closable
                @close="removeFilter(index)"
              >
                <span v-if="tag">
                  {{ tag.field }}:
                  <span style="font-weight: bold">{{ tag.value }}</span>
                </span>
              </el-tag>
            </div>
            <div class="product-list-actions">
              <template v-if="isBulkEditMode">
                <base-button
                  size="sm"
                  type="success"
                  :loading="savingEdits"
                  :disabled="loadingEditRows"
                  @click="saveAllEdits()"
                >
                  Save All Changes
                </base-button>
                <base-button
                  size="sm"
                  type="default"
                  :disabled="savingEdits"
                  @click="cancelBulkEdit()"
                >
                  Cancel
                </base-button>
              </template>
              <template v-else>
                <base-button
                  size="sm"
                  type="warning"
                  :loading="loadingEditRows"
                  :disabled="!tableData.length"
                  @click="enterBulkEditMode()"
                >
                  Edit All Rows
                </base-button>
                <el-popover
                  v-model="filterPopUpVisible"
                  placement="left"
                  width="550"
                >
                  <table-filter @apply="applyFilters" />
                  <base-button slot="reference" size="sm" type="primary">
                    Add Filter
                  </base-button>
                </el-popover>
                <base-button
                  size="sm"
                  type="primary"
                  @click="handleProductExport()"
                >
                  Export Products
                </base-button>
                <base-button
                  size="sm"
                  type="primary"
                  @click="handleProductImport()"
                >
                  Import Products
                </base-button>
              </template>
            </div>
          </div>
        </div>

        <div v-if="isBulkEditMode" class="bulk-edit-banner">
          <span>
            <i class="fas fa-edit" />
            Bulk edit mode — update prices and product details inline, then save
            all changes.
          </span>
          <span v-if="loadingEditRows">Loading product details...</span>
        </div>

        <div class="product-table-wrapper">
        <el-table
          v-loading="loadingEditRows || loadingListPrices"
          :data="displayRows"
          class="table-responsive table-flush product-inline-table"
          header-row-class-name="product-list-thead-row"
          :row-class-name="rowClassName"
        >
          <el-table-column
            label="Product Name"
            min-width="200"
            class-name="product-name-column"
            label-class-name="product-table-th product-name-th"
          >
            <template slot-scope="scope">
              <div v-if="isBulkEditMode">
                <el-input
                  v-model="scope.row.name"
                  type="textarea"
                  :autosize="{ minRows: 1, maxRows: 4 }"
                  size="mini"
                  class="product-name-input"
                />
                <el-input
                  v-model="scope.row.barcode"
                  size="mini"
                  placeholder="Barcode"
                  class="barcode-sub-input"
                />
              </div>
              <div v-else>
                <span class="product-name-cell">{{ scope.row.name }}</span>
                <div v-if="scope.row.barcode" class="barcode-sub-text">{{ scope.row.barcode }}</div>
              </div>
            </template>
          </el-table-column>

          <el-table-column
            label="Qty"
            min-width="90"
            label-class-name="product-table-th"
          >
            <template slot-scope="scope">
              <el-input
                v-if="isBulkEditMode"
                v-model="scope.row.quantity"
                size="mini"
                type="number"
              />
              <span v-else style="white-space:nowrap;">{{ scope.row.quantity }}</span>
            </template>
          </el-table-column>

          <el-table-column
            label="Tax"
            min-width="100"
            label-class-name="product-table-th"
          >
            <template slot-scope="scope">
              <span style="white-space:nowrap;">{{ getTaxLabel(scope.row) }}</span>
            </template>
          </el-table-column>

          <el-table-column
            v-for="priceType in productPriceTypes"
            :key="priceType.productPriceTypeId"
            :label="priceType.label"
            min-width="130"
            label-class-name="product-table-th"
          >
            <template slot-scope="scope">
              <div
                v-if="isBulkEditMode"
                class="price-edit-group"
              >
                <div class="price-edit-field">
                  <label>Price</label>
                  <el-input
                    v-model="getPriceEntry(scope.row, priceType).price"
                    size="mini"
                    type="number"
                  />
                </div>
                <div class="price-edit-field">
                  <label>MRP</label>
                  <el-input
                    v-model="getPriceEntry(scope.row, priceType).mrp"
                    size="mini"
                    type="number"
                  />
                </div>
                <div class="price-edit-field">
                  <label>Disc %</label>
                  <el-input
                    v-model="getPriceEntry(scope.row, priceType).defaultDiscount"
                    size="mini"
                    type="number"
                  />
                </div>
              </div>
              <div v-else class="price-view-group">
                <div class="price-row">
                  <span class="price-label">Price</span>
                  <span class="price-value price-amount">₹{{ formatAmount(getPriceDisplay(scope.row, priceType).price) }}</span>
                </div>
                <div class="price-row">
                  <span class="price-label">MRP</span>
                  <span class="price-value">₹{{ formatAmount(getPriceDisplay(scope.row, priceType).mrp) }}</span>
                </div>
                <div class="price-row">
                  <span class="price-label">Disc</span>
                  <span class="price-value price-disc">{{ formatDiscount(getPriceDisplay(scope.row, priceType).defaultDiscount) }}</span>
                </div>
              </div>
            </template>
          </el-table-column>

          <el-table-column
            label="Branch Rate"
            min-width="105"
            label-class-name="product-table-th branch-rate-th"
            class-name="branch-rate-column"
          >
            <template slot-scope="scope">
              <div v-if="isBulkEditMode">
                <el-input
                  v-if="scope.row.metadata"
                  v-model="scope.row.metadata.branch_price"
                  size="mini"
                  type="number"
                  placeholder="0"
                />
              </div>
              <div v-else>
                <span
                  v-if="scope.row.metadata && scope.row.metadata.branch_price != null && scope.row.metadata.branch_price !== ''"
                  class="branch-rate-badge"
                >₹{{ scope.row.metadata.branch_price }}</span>
                <span v-else class="text-muted">—</span>
              </div>
            </template>
          </el-table-column>

          <el-table-column
            v-if="!isBulkEditMode"
            label="Actions"
            width="130"
            align="center"
            label-class-name="product-table-th"
          >
            <template slot-scope="scope">
              <div class="actions-cell">
                <el-dropdown
                  @command="command => handleAction(scope.row, command)"
                >
                  <el-button type="primary" size="mini">
                    Actions
                    <i class="el-icon-arrow-down el-icon--right" />
                  </el-button>
                  <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item command="edit">Full Edit</el-dropdown-item>
                    <el-dropdown-item command="barcode">
                      Print Barcode
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </div>
            </template>
          </el-table-column>
        </el-table>
        </div>

        <div class="card-footer">
          <div
            v-if="meta"
            class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
          >
            <div>
              <p class="card-category">
                Showing {{ meta.from + 1 }} to {{ meta.to }} of
                {{ meta.total }} entries
                <span v-if="isBulkEditMode" class="text-warning">
                  &nbsp;· editing current page
                </span>
              </p>
            </div>
            <pagination :meta="meta" @paginate="setPagination" />
          </div>
        </div>
      </div>
    </div>

    <el-dialog
      :visible.sync="showImportFileModal"
      title="Upload Products"
      width="36%"
      :close-on-click-modal="!isFileUploadingLoader"
      :show-close="!isFileUploadingLoader"
    >
      <!-- File picker -->
      <div v-if="!isFileUploadingLoader">
        <!-- Branch selector -->
        <div class="mb-4">
          <label class="block text-xs font-medium text-gray-600 mb-1">
            Branch <span class="text-red-500">*</span>
            <span class="text-gray-400 font-normal ml-1">— quantity, min qty &amp; order qty will be updated for this branch only</span>
          </label>
          <select
            v-model="importBranchId"
            class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            :class="{ 'border-red-400': importBranchError }"
          >
            <option value="">Select branch...</option>
            <option v-for="b in importBranches" :key="b.id" :value="b.id">
              {{ b.name }}{{ b.is_default ? ' (default)' : '' }}
            </option>
          </select>
          <p v-if="importBranchError" class="text-red-500 text-xs mt-1">Please select a branch before uploading.</p>
        </div>

        <el-upload
          action
          class="upload-demo"
          :on-change="handleFileUpload"
          :file-list="fileList"
          :auto-upload="false"
          drag
          accept=".xlsx,.xls"
        >
          <i class="el-icon-upload" style="font-size:40px;color:#6698ff;margin-bottom:8px;"></i>
          <div class="el-upload__text" style="font-size:14px;">Drop your Excel file here or <em>click to browse</em></div>
          <div slot="tip" class="el-upload__tip" style="margin-top:6px;">Only <strong>.xlsx</strong> files are supported</div>
        </el-upload>
      </div>

      <!-- Import progress -->
      <div v-else class="import-progress-panel">
        <div class="import-file-row">
          <i class="el-icon-document" style="font-size:20px;color:#6698ff;flex-shrink:0;"></i>
          <div class="import-file-meta">
            <span class="import-file-name">{{ importFileName }}</span>
            <span class="import-file-size">{{ importFileSize }}</span>
          </div>
        </div>

        <div class="import-stage-label">{{ importStageMessage }}</div>

        <div class="import-progress-track">
          <div class="import-progress-fill" :style="{ width: importProgress + '%' }"></div>
        </div>

        <div class="import-progress-footer">
          <span class="import-pct">
            <template v-if="importTotal > 0">{{ importProcessed }} / {{ importTotal }} ({{ Math.round(importProgress) }}%)</template>
            <template v-else>{{ Math.round(importProgress) }}%</template>
          </span>
          <span class="import-elapsed">{{ importElapsed }}s elapsed</span>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { format } from "date-fns";
import { apiHelper } from "@/utils/helpers";
import endpoints from "@/utils/constants";
import {
  Table,
  TableColumn,
  DropdownMenu,
  DropdownItem,
  Dropdown,
  Button,
  Tag,
  Upload,
  Dialog,
  Input,
  Popover,
  MessageBox
} from "element-ui";
import { mapGetters, mapActions, mapMutations } from "vuex";
import headerComponent from "../Components/HeaderComponent";
import pagination from "@/components/AmsaPagination";
import tableFilter from "@/components/TableFilterComponent";
import ProductFilter from "../../components/ProductTableFilter";

export default {
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
    [Button.name]: Button,
    [Upload.name]: Upload,
    [Dialog.name]: Dialog,
    [Input.name]: Input,
    [Popover.name]: Popover,
    "header-component": headerComponent,
    pagination,
    "table-filter-component": tableFilter,
    "table-filter": ProductFilter,
    [Tag.name]: Tag
  },
  data() {
    return {
      fileList: [],
      pageSize: 10,
      filterPopUpVisible: false,
      filters: [],
      showImportFileModal: false,
      isFileUploadingLoader: false,
      isBulkEditMode: false,
      loadingEditRows: false,
      savingEdits: false,
      loadingListPrices: false,
      editableRows: [],
      originalRowsSnapshot: "",
      listProductDetailsById: {},
      importFileName: '',
      importFileSize: '',
      importProgress: 0,
      importProcessed: 0,
      importTotal: 0,
      importElapsed: 0,
      _importElapsedTimer: null,
      _progressPoller: null,
      importBranchId: '',
      importBranchError: false,
      importBranches: []
    };
  },
  computed: {
    ...mapGetters({
      products: "product/getProducts",
      productPriceTypes: "product/getProductPriceTypes"
    }),
    importStageMessage() {
      if (this.importProgress >= 100) return 'Import complete!';
      if (this.importTotal > 0) return `Processing ${this.importProcessed} of ${this.importTotal} products...`;
      return 'Uploading file to server...';
    },
    tableData() {
      return this.products.rows || [];
    },
    meta() {
      return this.products.meta;
    },
    displayRows() {
      return this.isBulkEditMode ? this.editableRows : this.tableData;
    },
    hasUnsavedChanges() {
      if (!this.isBulkEditMode) {
        return false;
      }
      return this.originalRowsSnapshot !== JSON.stringify(this.editableRows);
    }
  },
  watch: {
    filters: {
      deep: true,
      handler(value) {
        this.restructureFilters(value);
      }
    }
  },
  mounted() {
    this.getProductPriceTypes();
    if (typeof window !== "undefined") {
      window.scrollTo(0, 0);
    }
    const data = window.localStorage.getItem("productFilters");
    if (data != null) {
      try {
        const parsedData = JSON.parse(data);
        if (Array.isArray(parsedData) && parsedData.length) {
          this.filters.push(...parsedData);
          return;
        }
      } catch (_) {
        /* ignore invalid saved filters */
      }
    }
    this.paginate();
  },
  methods: {
    ...mapActions({
      getProducts: "product/getProducts",
      getProduct: "product/getProduct",
      exportProductList: "product/exportProductList",
      importProductFile: "product/importProductFile",
      getProductPriceTypes: "product/getProductPriceTypes",
      bulkFetchProductsForEdit: "product/bulkFetchProductsForEdit",
      bulkUpdateProducts: "product/bulkUpdateProducts"
    }),
    ...mapMutations({ setBarcode: "product/setBarcode" }),

    formatAmount(value) {
      if (value === null || value === undefined || value === "") {
        return "—";
      }
      const amount = parseFloat(value);
      return Number.isFinite(amount) ? amount : "—";
    },

    formatDiscount(value) {
      if (value === null || value === undefined || value === "") {
        return "—";
      }
      const amount = parseFloat(value);
      return Number.isFinite(amount) ? `${amount}%` : "—";
    },

    getTaxLabel(row) {
      if (row.tax && row.tax.label) {
        return row.tax.label;
      }
      return "—";
    },

    findPriceInList(prices, priceType) {
      if (!prices || !prices.length) {
        return null;
      }

      return prices.find(price => {
        const typeId =
          price.productPriceTypeId ||
          (price.productPriceType && price.productPriceType.productPriceTypeId);
        if (typeId && typeId === priceType.productPriceTypeId) {
          return true;
        }

        const label =
          price.productPriceTypeLabel ||
          (price.productPriceType && price.productPriceType.label);
        return (
          label &&
          priceType.label &&
          label.toLowerCase() === priceType.label.toLowerCase()
        );
      });
    },

    getPriceDisplay(row, priceType) {
      const details = this.listProductDetailsById[row.productId];
      const prices = details && details.prices ? details.prices : [];
      const entry = this.findPriceInList(prices, priceType);

      if (entry) {
        return {
          price: entry.price,
          mrp: entry.mrp != null && entry.mrp !== "" ? entry.mrp : row.mrp,
          defaultDiscount: entry.defaultDiscount
        };
      }

      return {
        price: null,
        mrp: row.mrp,
        defaultDiscount: null
      };
    },

    getPriceEntry(row, priceType) {
      if (!this.isBulkEditMode) {
        return this.getPriceDisplay(row, priceType);
      }

      if (!row.prices) {
        this.$set(row, "prices", []);
      }

      let entry = row.prices.find(
        price => price.productPriceTypeId === priceType.productPriceTypeId
      );

      if (!entry) {
        entry = {
          productPriceTypeId: priceType.productPriceTypeId,
          price: 0,
          mrp: 0,
          defaultDiscount: 0,
          fromQuantity: 1,
          startDate: format(new Date(), "YYYY-MM-DD")
        };
        row.prices.push(entry);
      }

      return entry;
    },

    normalizeProductForEdit(product) {
      const normalized = JSON.parse(JSON.stringify(product));

      if (!normalized.prices) normalized.prices = [];
      if (!normalized.metadata) normalized.metadata = {};
      if (normalized.metadata.branch_price == null) normalized.metadata.branch_price = '';

      this.productPriceTypes.forEach(priceType => {
        this.getPriceEntry(normalized, priceType);
      });

      return normalized;
    },

    async loadListProductDetails(productIds = null) {
      const ids =
        productIds ||
        this.tableData.map(row => row.productId).filter(id => id);

      if (!ids.length) {
        this.listProductDetailsById = {};
        return;
      }

      this.loadingListPrices = true;
      try {
        const products = await this.bulkFetchProductsForEdit(ids);
        const detailsMap = {};
        products.forEach(product => {
          detailsMap[product.productId] = product;
        });
        this.listProductDetailsById = detailsMap;
      } catch (error) {
        console.error("Failed to load product prices for list view:", error);
      } finally {
        this.loadingListPrices = false;
      }
    },

    rowClassName({ row }) {
      if (!this.isBulkEditMode) {
        return "";
      }
      const original = this.findOriginalRow(row.productId);
      if (!original) {
        return "edited-row";
      }
      return JSON.stringify(original) !== JSON.stringify(row) ? "edited-row" : "";
    },

    findOriginalRow(productId) {
      try {
        const originalRows = JSON.parse(this.originalRowsSnapshot);
        return originalRows.find(row => row.productId === productId);
      } catch (e) {
        return null;
      }
    },

    buildBulkUpdatePayload(row) {
      return {
        productId: row.productId,
        name: row.name,
        barcode: row.barcode,
        purchasePrice: row.purchasePrice,
        quantity: row.quantity,
        metadata: row.metadata || {},
        prices: (row.prices || []).map(price => ({
          productPriceId: price.productPriceId,
          productPriceTypeId: price.productPriceTypeId,
          price: price.price,
          mrp: price.mrp,
          defaultDiscount: price.defaultDiscount,
          fromQuantity: price.fromQuantity,
          startDate: price.startDate,
          endDate: price.endDate
        }))
      };
    },

    async enterBulkEditMode() {
      if (!this.tableData.length) {
        return;
      }

      this.loadingEditRows = true;
      try {
        const productIds = this.tableData
          .map(row => row.productId)
          .filter(id => id);

        if (!productIds.length) {
          throw new Error("No valid products found on this page.");
        }

        const allCached = productIds.every(
          id => this.listProductDetailsById[id]
        );
        const products = allCached
          ? productIds.map(id => this.listProductDetailsById[id])
          : await this.bulkFetchProductsForEdit(productIds);

        if (!allCached) {
          products.forEach(product => {
            this.$set(this.listProductDetailsById, product.productId, product);
          });
        }

        this.editableRows = products.map((product, index) => {
          const normalized = this.normalizeProductForEdit(product);
          if (!normalized.tax && this.tableData[index].tax) {
            normalized.tax = this.tableData[index].tax;
          }
          return normalized;
        });
        this.originalRowsSnapshot = JSON.stringify(this.editableRows);
        this.isBulkEditMode = true;
      } catch (error) {
        const message =
          (error.response &&
            error.response.data &&
            (error.response.data.message ||
              (error.response.data.errors &&
                Object.values(error.response.data.errors)[0] &&
                Object.values(error.response.data.errors)[0][0]))) ||
          error.message ||
          "Failed to load products for editing. Please try again.";
        this.$notify({
          message,
          type: "danger",
          title: "Error"
        });
      } finally {
        this.loadingEditRows = false;
      }
    },

    cancelBulkEdit() {
      if (this.hasUnsavedChanges) {
        MessageBox.confirm(
          "Discard all unsaved changes on this page?",
          "Cancel editing",
          {
            confirmButtonText: "Discard",
            cancelButtonText: "Keep editing",
            type: "warning"
          }
        )
          .then(() => {
            this.resetBulkEditState();
          })
          .catch(() => {});
        return;
      }
      this.resetBulkEditState();
    },

    resetBulkEditState() {
      this.isBulkEditMode = false;
      this.editableRows = [];
      this.originalRowsSnapshot = "";
    },

    async saveAllEdits() {
      if (!this.editableRows.length) {
        return;
      }

      const changedRows = this.editableRows.filter(row => {
        const original = this.findOriginalRow(row.productId);
        return !original || JSON.stringify(original) !== JSON.stringify(row);
      });

      if (!changedRows.length) {
        this.$notify({
          message: "No changes to save.",
          type: "info",
          title: "Info"
        });
        return;
      }

      this.savingEdits = true;

      try {
        const result = await this.bulkUpdateProducts(
          changedRows.map(row => this.buildBulkUpdatePayload(row))
        );

        if (result.failed && result.failed.length > 0) {
          const failedNames = result.failed
            .map(item => {
              const row = this.editableRows.find(
                product => product.productId === item.product_id
              );
              return row ? row.name : item.product_id;
            })
            .join(", ");

          this.$notify({
            message: `Saved ${result.updated} product(s). Failed: ${failedNames}`,
            type: "warning",
            title: "Partial failure"
          });
        } else {
          this.$notify({
            message: `Saved ${result.updated} product(s) successfully.`,
            type: "success",
            title: "Success"
          });
          this.resetBulkEditState();
          this.restructureFilters(this.filters);
        }
      } catch (error) {
        this.$notify({
          message: "Failed to save products. Please try again.",
          type: "danger",
          title: "Error"
        });
      } finally {
        this.savingEdits = false;
      }
    },

    handleAction(product, command) {
      if (command === "edit") {
        this.getProduct(product.productId);
        this.$router.push("/products/add/" + product.productId);
      }
      if (command === "barcode") {
        this.setBarcode(product.barcode);
        this.showBarcode();
      }
    },

    showBarcode() {
      this.$router.push("/products/barcode");
    },

    setPagination(data) {
      if (this.isBulkEditMode && this.hasUnsavedChanges) {
        MessageBox.confirm(
          "You have unsaved edits. Change page and discard them?",
          "Unsaved changes",
          {
            confirmButtonText: "Discard and continue",
            cancelButtonText: "Stay on page",
            type: "warning"
          }
        )
          .then(() => {
            this.resetBulkEditState();
            this.pageSize = data.perPage;
            this.restructureFilters(this.filters, data.page);
          })
          .catch(() => {});
        return;
      }

      if (this.isBulkEditMode) {
        this.resetBulkEditState();
      }
      this.pageSize = data.perPage;
      this.restructureFilters(this.filters, data.page);
    },

    async paginate(page = 1, filters = {}) {
      const query = {
        page,
        perPage: this.pageSize,
        ...filters
      };
      await this.getProducts(query);
      await this.loadListProductDetails();
    },

    applyFilters(filters) {
      if (filters.length > 1) {
        this.filters.push(...filters);
      } else {
        this.filters.push(filters);
      }
      window.localStorage.setItem(
        "productFilters",
        JSON.stringify(this.filters)
      );
      this.filterPopUpVisible = false;
    },

    removeFilter(tagIndex) {
      if (
        this.filters[tagIndex].field === "attribute" ||
        this.filters[tagIndex].field === "value"
      ) {
        const selectedItem = this.filters.find(
          (_, index) => index === tagIndex
        );
        const item = this.filters.find(i => i.type === selectedItem.type);
        this.filters.splice(tagIndex, 1);
        const index = this.filters.indexOf(item);
        this.filters.splice(index, 1);
      } else {
        this.filters.splice(tagIndex, 1);
      }
      if (this.filters.length > 0) {
        window.localStorage.setItem(
          "productFilters",
          JSON.stringify(this.filters)
        );
      } else {
        window.localStorage.removeItem("productFilters");
      }
    },

    restructureFilters(value, page = 1) {
      if (this.isBulkEditMode) {
        this.resetBulkEditState();
      }

      const filtered = {};
      value.forEach(i => {
        if (typeof i.value != "undefined") {
          filtered[i.field] = i.value;
        }
      });
      if (Object.entries(filtered).length > 0) {
        this.paginate(page, filtered);
      } else {
        this.paginate(page);
      }
    },

    handleProductExport() {
      const query = {};
      this.filters.forEach(i => {
        if (typeof i.value != "undefined") {
          query[i.field] = i.value;
        }
      });
      this.exportProductList(query).then(res => {
        const url = URL.createObjectURL(
          new Blob([res.data], {
            type: "application/vnd.ms-excel"
          })
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "products");
        document.body.appendChild(link);
        link.click();
        this.$notify({
          message: "Products Exported Successfully",
          type: "success",
          title: "Success"
        });
      });
    },

    handleProductImport() {
      this.showImportFileModal = true;
      this.importBranchId = '';
      this.importBranchError = false;
      if (!this.importBranches.length) {
        apiHelper
          .call("get", `${endpoints.LARAVEL_BASE_URL}/api/v1/branches`)
          .then(res => {
            this.importBranches = (res.data && res.data.data) || [];
            const def = this.importBranches.find(b => b.is_default);
            if (def) this.importBranchId = def.id;
          })
          .catch(() => {});
      } else {
        const def = this.importBranches.find(b => b.is_default);
        if (def && !this.importBranchId) this.importBranchId = def.id;
      }
    },

    handleFileUpload(file) {
      const fileType = file.raw.name.split(".").pop().toLowerCase();
      if (fileType !== "xlsx" && fileType !== "xls") {
        this.$notify({
          showClose: true,
          message: "File should be of type .xlsx",
          type: "error",
          title: "Error"
        });
        return;
      }

      if (!this.importBranchId) {
        this.importBranchError = true;
        return;
      }
      this.importBranchError = false;

      const importId = `imp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      this.importFileName    = file.raw.name;
      this.importFileSize    = this._formatFileSize(file.raw.size);
      this.importProgress    = 0;
      this.importProcessed   = 0;
      this.importTotal       = 0;
      this.importElapsed     = 0;
      this.isFileUploadingLoader = true;

      this._importElapsedTimer = setInterval(() => { this.importElapsed++; }, 1000);

      // POST returns immediately (job is queued), then polling drives progress
      this.importProductFile({ file: file.raw, branchId: this.importBranchId, importId })
        .then(() => {
          this._startProgressPolling(importId);
        })
        .catch(() => {
          this._stopImportProgress(false);
          this.isFileUploadingLoader = false;
        });
    },

    _formatFileSize(bytes) {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },

    _startProgressPolling(importId) {
      const poll = () => {
        apiHelper
          .call("get", `${endpoints.LARAVEL_BASE_URL}/api/v1/import/status/${importId}`)
          .then(async res => {
            const { status, processed = 0, total = 0 } = res.data || {};
            this.importProcessed = processed;
            this.importTotal     = total;
            if (total > 0) {
              this.importProgress = Math.min(99, Math.round((processed / total) * 100));
            }

            if (status === 'completed') {
              this._stopImportProgress(true);
              await this.$nextTick();
              await new Promise(r => setTimeout(r, 600));
              this.isFileUploadingLoader = false;
              this.showImportFileModal = false;
              await this.paginate();
              this.$notify({ showClose: true, message: "Products imported successfully!", type: "success", title: "Success" });
            } else if (status === 'failed') {
              this._stopImportProgress(false);
              this.isFileUploadingLoader = false;
              this.$notify({ showClose: true, message: res.data.error || "Import failed.", type: "error", title: "Error" });
            } else {
              // queued or processing — poll again
              this._progressPoller = setTimeout(poll, 500);
            }
          })
          .catch(() => {
            this._progressPoller = setTimeout(poll, 500);
          });
      };

      poll(); // first poll immediately after job is queued
    },

    _stopImportProgress(success) {
      clearTimeout(this._progressPoller);
      clearInterval(this._importElapsedTimer);
      this._progressPoller     = null;
      this._importElapsedTimer = null;
      this.importProgress = success ? 100 : 0;
    }
  }
};
</script>

<style>
.import-progress-panel {
  padding: 8px 0 4px;
}

.import-file-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: #f0f4ff;
  border-radius: 8px;
  margin-bottom: 20px;
}

.import-file-meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.import-file-name {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.import-file-size {
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
}

.import-stage-label {
  font-size: 13px;
  color: #475569;
  margin-bottom: 10px;
  min-height: 18px;
}

.import-progress-track {
  height: 8px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
}

.import-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #6698ff, #4f7fe0);
  border-radius: 999px;
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.import-progress-footer {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  font-size: 11px;
  color: #94a3b8;
}

.import-pct {
  font-weight: 600;
  color: #6698ff;
}

.product-list-page__toolbar {
  position: relative;
  z-index: 5;
  flex-shrink: 0;
}

.product-list-page__toolbar-right {
  min-width: 0;
}

.product-list-page__filters {
  margin-bottom: 8px;
}

.product-list-page .product-list-actions {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
}

.product-list-page .product-table-wrapper {
  overflow-x: auto;
}

.product-inline-table .product-list-thead-row th {
  background-color: #f6f9fc;
  vertical-align: middle;
}

.product-inline-table .product-table-th .cell {
  white-space: nowrap;
  line-height: 1.3;
  padding-top: 12px;
  padding-bottom: 12px;
  display: flex;
  align-items: center;
  min-height: 23px;
}

.product-inline-table .product-name-th .cell {
  white-space: normal;
  word-break: break-word;
}

.product-inline-table .product-name-column .cell {
  white-space: normal !important;
  word-break: break-word;
  line-height: 1.4;
  align-items: flex-start;
}


.product-name-cell {
  display: block;
  white-space: normal;
  word-break: break-word;
  line-height: 1.4;
}

.barcode-sub-text {
  margin-top: 3px;
  font-size: 11px;
  color: #8898aa;
  letter-spacing: 0.03em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 220px;
}

.barcode-sub-input {
  margin-top: 4px;
}

.product-name-input textarea {
  white-space: normal;
  word-break: break-word;
  line-height: 1.4;
}

.actions-cell {
  display: flex;
  justify-content: center;
  align-items: center;
}

.bulk-edit-banner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background: #fff8e6;
  border-bottom: 1px solid #f0e2b6;
  color: #8a6d1d;
  font-size: 13px;
}

.product-inline-table .el-input__inner {
  padding-left: 8px;
  padding-right: 8px;
}

.price-edit-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.price-edit-field label {
  display: block;
  font-size: 10px;
  color: #8898aa;
  margin-bottom: 2px;
  text-transform: uppercase;
}

.price-view-group {
  font-size: 12px;
  line-height: 1.4;
}

.price-row {
  display: flex;
  align-items: baseline;
  gap: 5px;
  margin-bottom: 1px;
}

.price-label {
  font-size: 10px;
  font-weight: 600;
  color: #adb5bd;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  min-width: 28px;
}

.price-value {
  font-weight: 600;
  color: #32325d;
}

.price-amount {
  color: #2dce89;
}

.price-disc {
  color: #fb6340;
}

.product-inline-table .edited-row > td {
  background: #fffdf5 !important;
}

.product-inline-table .el-table__row:hover > td {
  background: #f0f7ff !important;
}

.branch-rate-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  background: #e0f2fe;
  color: #0369a1;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.branch-rate-column .cell {
  white-space: nowrap;
}

.branch-rate-th .cell {
  color: #0369a1 !important;
}

.table-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.table-filters .el-tag {
  margin-left: 0;
}
</style>
